<template>
  <div class="card" style="min-height: calc(100vh - 185px)">
    <PageHeader
        title="Due Collection Calendar"
        subtitle="Date wise client dues with one click collection"
        :show-btn="false"
    >
      <template #extra-btn>
        <button type="button" class="export-btn" @click="onClickToExport">
          <i class="fa-solid fa-file-excel"></i>
          Export
        </button>
        <FilterPopover
            ref="filterPopoverRef"
            :active-count="activeFilterCount"
            popover-width="360px"
            @open="onOpenFilter"
            @apply="onApplyFilters"
            @clear="onClearFilters"
        >
          <div class="col-12">
            <label class="filter-label">Customer</label>
            <AsyncSelect
                placeholder="Any customer"
                v-model="draftFilters.contactProfile"
                :api-service="fetchContactProfiles"
                :additional-query="{ with_mobile: 1 }"
                :format-label="formatPatientLabel"
            />
          </div>
          <div class="col-12 mt-1">
            <label class="filter-label">Bill Type</label>
            <v-select
                multiple
                placeholder="Select Bill Type"
                v-model="draftFilters.billType"
                :options="billTypeList"
                label="label"
                :reduce="label => label.value"
            />
          </div>
          <div class="col-12 mt-1">
            <label class="filter-label">Business Unit</label>
            <v-select
                placeholder="Select Business"
                v-model="draftFilters.businessUnit"
                :options="business"
                label="name"
                :reduce="name => name.id"
            />
          </div>
        </FilterPopover>
      </template>
    </PageHeader>

    <div class="due-range-cards mt-2 px-2">
      <div
          v-for="item in dueRanges"
          :key="item.key"
          class="due-range-card"
          :class="{ 'is-active': dueRange === item.key }"
          @click="onSelectDueRange(item.key)"
      >
        <span class="due-range-label">{{ item.name }}</span>
        <span class="due-range-amount">{{ commaFormat(getRangeAmount(item.key)) }}</span>
        <span class="due-range-count">{{ getRangeCount(item.key) }} bill(s)</span>
      </div>
    </div>

    <div v-if="activeFilterChips.length" class="filter-chips mt-1 px-2">
      <span v-for="chip in activeFilterChips" :key="chip.key" class="filter-chip">
        <span class="filter-chip-label">{{ chip.label }}:</span>
        {{ chip.text }}
        <button type="button" class="filter-chip-remove" @click="removeFilter(chip.key)">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </span>
      <button type="button" class="filter-chip-clear" @click="onClearFilters">Clear all</button>
    </div>

    <div class="toolbar mt-2 px-2">
      <div class="d-flex align-items-center gap-1">
        <button type="button" class="btn-nav" @click="onClickPrev">Prev</button>
        <button type="button" class="btn-nav" @click="onClickToday">Today</button>
        <button type="button" class="btn-nav" @click="onClickNext">Next</button>
        <span class="range-title">{{ rangeTitle }}</span>
      </div>
      <div class="due-legend">
        <span v-for="item in legendItems" :key="item.bucket" class="legend-item">
          <span class="legend-dot" :style="legendStyle(item.bucket)"></span>{{ item.name }}
        </span>
      </div>
      <div class="btn-group-segment">
        <button
            v-for="span in viewSpans"
            :key="span.key"
            type="button"
            class="segment-btn"
            :class="{ 'is-active': viewSpan === span.key }"
            @click="onSelectViewSpan(span.key)"
        >{{ span.name }}</button>
      </div>
    </div>

    <div class="due-matrix-wrap mt-2 mx-2">
      <table class="table due-matrix mb-0">
        <thead>
        <tr>
          <th class="sticky-col sticky-col-name is-sortable" @click="onSort('contact_name')">
            Name (ID)<span class="sort-arrow">{{ sortArrow('contact_name') }}</span><br/>Mobile No
          </th>
          <th class="sticky-col sticky-col-range text-end is-sortable" @click="onSort('range_due')">
            Range Due<span class="sort-arrow">{{ sortArrow('range_due') }}</span>
          </th>
          <th class="sticky-col sticky-col-total text-end is-sortable" @click="onSort('total_due')">
            Total Due<span class="sort-arrow">{{ sortArrow('total_due') }}</span>
          </th>
          <th
              v-for="day in visibleColumns"
              :key="day.date"
              class="text-center day-col"
              :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
          >
            {{ day.label }}
            <div class="day-weekday">{{ day.weekdayLabel }}</div>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in matrixRows" :key="row.contact_profile_id">
          <td class="sticky-col sticky-col-name">
            <span class="client-name" @click="onClickClient(row)">{{ row.contact_name }}</span>
            <span v-if="row.contact_serial_no" class="client-serial"> ({{ row.contact_serial_no }})</span>
            <div class="client-mobile">{{ row.contact_mobile_no }}</div>
          </td>
          <td class="sticky-col sticky-col-range text-end fw-bold">{{ commaFormat(row.range_due) }}</td>
          <td class="sticky-col sticky-col-total text-end lifetime-due">{{ commaFormat(row.lifetime_due) }}</td>
          <td
              v-for="day in visibleColumns"
              :key="day.date"
              class="text-end day-col"
              :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
          >
            <span
                v-if="row.cells[day.date]"
                class="due-cell"
                :style="cellStyle(row.cells[day.date])"
                @click="onClickCell(row, row.cells[day.date])"
            >
              <span class="due-cell-amount">
                {{ commaFormat(row.cells[day.date].total_due) }}
                <span v-if="row.cells[day.date].is_due_date_fallback" class="due-cell-flag">~</span>
              </span>
              <span v-if="cellMeta(row.cells[day.date])" class="due-cell-meta">{{ cellMeta(row.cells[day.date]) }}</span>
            </span>
          </td>
        </tr>
        <tr v-if="!matrixRows.length">
          <td :colspan="visibleColumns.length + 3" class="empty-cell">
            <i class="fa-regular fa-calendar-check empty-icon"></i>
            <div class="empty-title">No due found for this period</div>
            <div class="empty-hint">Try another date range or clear the filters</div>
          </td>
        </tr>
        </tbody>
        <tfoot v-if="matrixRows.length">
        <tr class="total-row">
          <th class="sticky-col sticky-col-name">Total</th>
          <th class="sticky-col sticky-col-range text-end">{{ commaFormat(grandRangeTotal) }}</th>
          <th class="sticky-col sticky-col-total text-end">{{ commaFormat(grandLifetimeTotal) }}</th>
          <th
              v-for="day in visibleColumns"
              :key="day.date"
              class="text-end day-col"
              :class="{ 'is-today': day.isToday, 'is-weekend': day.isWeekend }"
          >
            <span v-if="columnTotals[day.date]">{{ commaFormat(columnTotals[day.date]) }}</span>
          </th>
        </tr>
        </tfoot>
      </table>
    </div>

    <div class="px-2 mt-2">
      <Pagination :offset="clientOffset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <DueCollectionDetailModal
        v-if="store.state.isModalOpenTwo && selectedEntry"
        :entry="selectedEntry"
    />

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import PageHeader from '@/components/atom/PageHeader.vue'
import FilterPopover from '@/components/atom/FilterPopover.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import DueCollectionDetailModal
  from '@/components/molecule/company/inventory/due-collection-calendar/DueCollectionDetailModal.vue'
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import handleCBusinesses from '@/services/modules/businesses'
import handleInventory from '@/services/modules/inventory'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import figureFormatter from '@/services/utils/figureFormatter'
import { getDueCollectionStatusColor } from '@/services/utils/global'

const route = useRoute()
const router = useRouter()
const store = useStore()

const { fetchDueCollectionCalendar } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchBusinessList } = handleCBusinesses()
const { fetchHome } = handleInventory()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { commaFormat } = figureFormatter()

const loading = ref(false)
const events = ref([])
const summary = ref(null)
const lifetimeTotals = ref({})
const business = ref([])
const billTypeList = ref([])
const selectedEntry = ref(null)
const filterPopoverRef = ref(null)
const clientOffset = ref(20)
const anchorDate = ref(resolveInitialAnchor())
const viewSpan = ref('month')
const dueRange = ref('total')
const sortBy = ref('earliest_due_date')
const sortDir = ref('asc')

const dueRanges = [
  { key: 'total', name: 'Total Due (All Time)' },
  { key: 'this_month', name: 'This Month' },
  { key: 'this_week', name: 'This Week' },
  { key: 'today', name: "Today's Due" },
  { key: 'tomorrow', name: "Tomorrow's Due" }
]

const viewSpans = [
  { key: 'day', name: 'Day' },
  { key: 'week', name: 'Week' },
  { key: 'month', name: 'Month' }
]

const legendItems = [
  { bucket: 'overdue', name: 'Overdue' },
  { bucket: 'due_today', name: 'Due Today' },
  { bucket: 'upcoming', name: 'Upcoming' }
]

const weekdayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const getEmptyFilters = () => {
  return {
    contactProfile: '',
    billType: null,
    businessUnit: null
  }
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const dayColumns = computed(() => {
  const columns = []
  const total = spanLength()
  const start = rangeStartDate()
  const todayKey = toDateKey(new Date())
  let index = 0
  while (index < total) {
    const current = addDays(start, index)
    const key = toDateKey(current)
    const weekday = current.getDay()
    columns.push({
      date: key,
      label: formatDayLabel(current),
      weekdayLabel: weekdayNames[weekday],
      isToday: key === todayKey,
      isWeekend: weekday === 5 || weekday === 6
    })
    index += 1
  }
  return columns
})

const filledDates = computed(() => {
  const dates = new Set()
  events.value.forEach(function (item) {
    dates.add(item.effective_due_date)
  })
  return dates
})

const visibleColumns = computed(() => {
  if (viewSpan.value !== 'month') return dayColumns.value
  const filled = dayColumns.value.filter(function (day) {
    return filledDates.value.has(day.date)
  })
  if (!filled.length) return dayColumns.value
  return filled
})

const matrixRows = computed(() => {
  const grouped = new Map()
  events.value.forEach(function (item) {
    const existing = grouped.get(item.contact_profile_id)
    if (existing) return appendCell(existing, item)
    grouped.set(item.contact_profile_id, buildRow(item))
  })
  return sortRows(Array.from(grouped.values()))
})

const columnTotals = computed(() => {
  const totals = {}
  visibleColumns.value.forEach(function (day) {
    totals[day.date] = 0
  })
  events.value.forEach(function (item) {
    if (!(item.effective_due_date in totals)) return
    totals[item.effective_due_date] += Number(item.total_due)
  })
  return totals
})

const grandRangeTotal = computed(() => {
  return matrixRows.value.reduce(function (carry, row) {
    return carry + row.range_due
  }, 0)
})

const grandLifetimeTotal = computed(() => {
  return matrixRows.value.reduce(function (carry, row) {
    return carry + row.lifetime_due
  }, 0)
})

const rangeTitle = computed(() => {
  const columns = dayColumns.value
  if (!columns.length) return ''
  if (columns.length === 1) return columns[0].label
  return columns[0].label + ' - ' + columns[columns.length - 1].label
})

const activeFilterChips = computed(() => {
  const chips = []
  const filters = appliedFilters.value
  if (filters.contactProfile) chips.push({ key: 'contactProfile', label: 'Customer', text: filters.contactProfile.name })
  if (filters.billType && filters.billType.length) chips.push({ key: 'billType', label: 'Bill Type', text: billTypeText(filters.billType) })
  if (filters.businessUnit) chips.push({ key: 'businessUnit', label: 'Business', text: businessText(filters.businessUnit) })
  return chips
})

function billTypeText(values) {
  return values.map(function (value) {
    const match = billTypeList.value.find(function (item) {
      return item.value === value
    })
    if (!match) return value
    return match.label
  }).join(', ')
}

function businessText(id) {
  const match = business.value.find(function (item) {
    return item.id === id
  })
  if (!match) return id
  return match.name
}

const activeFilterCount = computed(() => {
  return activeFilterChips.value.length
})

function buildRow(item) {
  const row = {
    contact_profile_id: item.contact_profile_id,
    contact_name: item.contact_name,
    contact_mobile_no: item.contact_mobile_no,
    contact_serial_no: item.contact_serial_no,
    range_due: 0,
    lifetime_due: getLifetimeDue(item.contact_profile_id),
    earliest_date: item.effective_due_date,
    cells: {}
  }
  appendCell(row, item)
  return row
}

function appendCell(row, item) {
  row.cells[item.effective_due_date] = item
  row.range_due += Number(item.total_due)
  if (item.effective_due_date < row.earliest_date) row.earliest_date = item.effective_due_date
}

function getLifetimeDue(contactProfileId) {
  const match = lifetimeTotals.value[contactProfileId]
  if (!match) return 0
  return Number(match.lifetime_due)
}

function sortRows(rows) {
  const direction = sortDirection()
  return rows.slice().sort(function (a, b) {
    return compareRows(a, b) * direction
  })
}

function sortDirection() {
  if (sortDir.value === 'desc') return -1
  return 1
}

function compareRows(a, b) {
  if (sortBy.value === 'contact_name') return String(a.contact_name).localeCompare(String(b.contact_name))
  if (sortBy.value === 'total_due') return a.lifetime_due - b.lifetime_due
  if (sortBy.value === 'range_due') return a.range_due - b.range_due
  return compareByEarliestDate(a, b)
}

function compareByEarliestDate(a, b) {
  if (a.earliest_date === b.earliest_date) return 0
  if (a.earliest_date < b.earliest_date) return -1
  return 1
}

function onSort(column) {
  if (sortBy.value !== column) {
    sortBy.value = column
    sortDir.value = 'asc'
    return
  }
  sortDir.value = flipSortDir()
}

function flipSortDir() {
  if (sortDir.value === 'asc') return 'desc'
  return 'asc'
}

function sortArrow(column) {
  if (sortBy.value !== column) return ''
  if (sortDir.value === 'asc') return ' ▲'
  return ' ▼'
}

function cellMeta(cell) {
  const parts = []
  if (cell.days_overdue > 0) parts.push(cell.days_overdue + 'd overdue')
  if (cell.invoice_count > 1) parts.push(cell.invoice_count + ' bills')
  return parts.join(' · ')
}

function spanLength() {
  if (viewSpan.value === 'day') return 1
  if (viewSpan.value === 'week') return 7
  return daysInMonth(anchorDate.value)
}

function rangeStartDate() {
  if (viewSpan.value === 'month') return startOfMonth(anchorDate.value)
  if (viewSpan.value === 'week') return startOfWeek(anchorDate.value)
  return new Date(anchorDate.value.getTime())
}

function daysInMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function startOfWeek(date) {
  const copy = new Date(date.getTime())
  copy.setDate(copy.getDate() - copy.getDay())
  return copy
}

function addDays(date, days) {
  const copy = new Date(date.getTime())
  copy.setDate(copy.getDate() + days)
  return copy
}

function toDateKey(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return date.getFullYear() + '-' + month + '-' + day
}

function formatDayLabel(date) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return String(date.getDate()).padStart(2, '0') + ' ' + months[date.getMonth()]
}

function resolveInitialAnchor() {
  if (route.query.anchor) return new Date(route.query.anchor)
  return new Date()
}

function legendStyle(bucket) {
  const color = getDueCollectionStatusColor(bucket)
  return 'background:' + color.bg + ';border-color:' + color.border
}

function cellStyle(cell) {
  const color = getDueCollectionStatusColor(cell.bucket)
  return 'background:' + color.bg + ';color:' + color.text + ';border-left:3px solid ' + color.border
}

function getRangeAmount(key) {
  if (!summary.value) return 0
  return summary.value[key + '_amount']
}

function getRangeCount(key) {
  if (!summary.value) return 0
  return summary.value[key + '_count']
}

function getBaseQuery() {
  const columns = dayColumns.value
  const filters = appliedFilters.value
  let query = '?company_id=' + route.params.companyId
  query += '&due_range=' + dueRange.value
  query += '&start_date=' + columns[0].date
  query += '&end_date=' + columns[columns.length - 1].date
  if (filters.contactProfile) query += '&contact_profile_id=' + filters.contactProfile.id
  if (filters.billType && filters.billType.length) query += '&bill_type=' + filters.billType
  if (filters.businessUnit) query += '&business_id=' + filters.businessUnit
  return query
}

function getQuery(skipSummary) {
  let query = getBaseQuery()
  query += '&client_offset=' + clientOffset.value
  if (route.query.page) query += '&client_page=' + route.query.page
  if (skipSummary) query += '&skip_summary=1'
  return query
}

function onClickToExport() {
  const token = localStorage.getItem('token')
  const url = process.env.VUE_APP_BASE_URL + '/export/inventory/due-collection-calendar' + getBaseQuery() + '&_token=' + token
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.click()
}

async function getCalendarData(skipSummary) {
  loading.value = true
  await fetchDueCollectionCalendar(getQuery(skipSummary)).then(function (res) {
    if (!res.status) {
      lifetimeTotals.value = {}
      events.value = []
      store.commit('resetPagination')
      return
    }
    lifetimeTotals.value = res.data.lifetime_totals
    events.value = res.data.events
    setPagination(res.data.clients)
    if (!skipSummary) summary.value = res.data.summary
  }).finally(function () {
    loading.value = false
  })
}

function setPagination(data) {
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

function refreshAll() {
  getCalendarData()
}

function refreshRows() {
  getCalendarData(true)
}

function syncAnchorToUrl() {
  const query = Object.assign({}, route.query)
  query.anchor = toDateKey(anchorDate.value)
  router.replace({ path: route.path, query: query })
}

function onSelectDueRange(key) {
  if (dueRange.value === key) return
  dueRange.value = key
  applyRangeView(key)
  resetPage()
  refreshAll()
}

function applyRangeView(key) {
  if (key === 'total') return
  anchorDate.value = rangeAnchorDate(key)
  viewSpan.value = rangeViewSpan(key)
  syncAnchorToUrl()
}

function rangeAnchorDate(key) {
  if (key === 'tomorrow') return addDays(new Date(), 1)
  return new Date()
}

function rangeViewSpan(key) {
  if (key === 'this_month') return 'month'
  if (key === 'this_week') return 'week'
  return 'day'
}

function onSelectViewSpan(key) {
  if (viewSpan.value === key) return
  viewSpan.value = key
  refreshRows()
}

function onClickPrev() {
  anchorDate.value = shiftAnchor(-1)
  syncAnchorToUrl()
  refreshRows()
}

function onClickNext() {
  anchorDate.value = shiftAnchor(1)
  syncAnchorToUrl()
  refreshRows()
}

function onClickToday() {
  anchorDate.value = new Date()
  syncAnchorToUrl()
  refreshRows()
}

function shiftAnchor(direction) {
  const current = anchorDate.value
  if (viewSpan.value === 'month') return new Date(current.getFullYear(), current.getMonth() + direction, 1)
  if (viewSpan.value === 'week') return addDays(current, direction * 7)
  return addDays(current, direction)
}

function onClickCell(row, cell) {
  selectedEntry.value = {
    contact_profile_id: row.contact_profile_id,
    contact_name: row.contact_name,
    contact_mobile_no: row.contact_mobile_no,
    effective_due_date: cell.effective_due_date,
    total_due: cell.total_due,
    invoice_count: cell.invoice_count
  }
  store.state.isModalOpenTwo = true
}

function onClickClient(row) {
  selectedEntry.value = {
    contact_profile_id: row.contact_profile_id,
    contact_name: row.contact_name,
    contact_mobile_no: row.contact_mobile_no,
    effective_due_date: '',
    total_due: row.total_due,
    invoice_count: 0
  }
  store.state.isModalOpenTwo = true
}

const onOpenFilter = () => {
  draftFilters.value = Object.assign({}, appliedFilters.value)
}

const onApplyFilters = () => {
  appliedFilters.value = Object.assign({}, draftFilters.value)
  resetPage()
  refreshAll()
}

const onClearFilters = () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  if (filterPopoverRef.value) filterPopoverRef.value.close()
  resetPage()
  refreshAll()
}

const removeFilter = (key) => {
  appliedFilters.value[key] = getEmptyFilters()[key]
  draftFilters.value[key] = getEmptyFilters()[key]
  resetPage()
  refreshAll()
}

function resetPage() {
  store.commit('resetPagination')
  const query = Object.assign({}, route.query)
  delete query.page
  router.replace({ path: route.path, query: query })
}

async function onPageChange(page) {
  const query = Object.assign({}, route.query)
  query.page = page
  await router.push({ path: route.path, query: query })
  refreshRows()
}

async function getBusinessList() {
  const res = await fetchBusinessList('?company_id=' + route.params.companyId)
  if (!res.status) return
  business.value = res.data
}

function getBillTypeList() {
  return fetchHome().then(function (res) {
    if (!res.data) return
    for (const value of Object.keys(res.data.hospital_invoice_types)) {
      billTypeList.value.push({
        value: value,
        label: res.data.hospital_invoice_types[value]
      })
    }
  })
}

onMounted(() => {
  store.state.isModalOpenTwo = false
  getBusinessList()
  getBillTypeList()
  getCalendarData()
})
</script>

<style scoped>
.export-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.9rem;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  color: #000;
  cursor: pointer;
  transition: background-color .14s ease, border-color .14s ease, box-shadow .14s ease;
}

.export-btn:hover {
  background: #f7f7fb;
  border-color: #7367f0;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .1);
}

.export-btn i {
  color: #1d7044;
}

.due-range-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.due-range-card {
  position: relative;
  flex: 1 1 160px;
  min-width: 150px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0.55rem 0.85rem;
  border: 1px solid #e9eaf0;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease;
}

.due-range-card::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: transparent;
  transition: background .16s ease;
}

.due-range-card:hover {
  border-color: #d9d7fb;
  box-shadow: 0 4px 14px rgba(16, 24, 40, .07);
  transform: translateY(-1px);
}

.due-range-card.is-active {
  border-color: #7367f0;
  background: linear-gradient(180deg, #f7f6ff 0%, #fff 70%);
  box-shadow: 0 4px 16px rgba(115, 103, 240, .16);
}

.due-range-card.is-active::before {
  background: #7367f0;
}

.due-range-label {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: #000;
}

.due-range-amount {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -.01em;
  font-variant-numeric: tabular-nums;
  color: #000;
  word-break: break-all;
}

.due-range-count {
  font-size: 0.78rem;
  color: #000;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 0.75rem;
}

.range-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #000;
  margin-left: 0.6rem;
  letter-spacing: -.01em;
}

.btn-group-segment {
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: #f2f3f7;
  gap: 2px;
}

.segment-btn {
  border: 0;
  border-radius: 8px;
  padding: 0.3rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.5;
  color: #000;
  background-color: transparent;
  box-shadow: none;
  cursor: pointer;
  transition: background-color .14s ease, box-shadow .14s ease;
}

.segment-btn:hover {
  background-color: rgba(255, 255, 255, .7);
}

.segment-btn.is-active {
  background-color: #fff;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .16);
}

.btn-nav {
  border: 1px solid #e4e5ec;
  border-radius: 9px;
  padding: 0.3rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.5;
  color: #000;
  background-color: #fff;
  cursor: pointer;
  transition: background-color .14s ease, border-color .14s ease, box-shadow .14s ease;
}

.btn-nav:hover {
  background-color: #f7f7fb;
  border-color: #cdcbf9;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .09);
}

.due-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  gap: 0.55rem 0.9rem;
  font-size: 0.85rem;
  color: #000;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid;
  display: inline-block;
}

.due-matrix-wrap {
  overflow: auto;
  max-width: 100%;
  max-height: calc(100vh - 430px);
  border: 1px solid #eceef3;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, .05);
  background: #fff;
}

.due-matrix {
  font-size: 0.88rem;
  margin-bottom: 0;
  color: #000;
  border-collapse: separate;
  border-spacing: 0;
}

.due-matrix th,
.due-matrix td {
  border: 0;
  border-bottom: 1px solid #f0f1f5;
  padding: 0.45rem 0.7rem;
}

.due-matrix th {
  background: #fafbfd;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #000;
  white-space: nowrap;
  vertical-align: middle;
  border-bottom: 1px solid #e7e9ef;
}

.due-matrix thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.due-matrix tbody tr {
  transition: background .12s ease;
}

.due-matrix tbody tr:hover td {
  background: #fafaff;
}

.due-matrix tbody tr:hover .sticky-col {
  background: #fafaff;
}

.due-matrix tbody tr:last-child td {
  border-bottom: 0;
}

.day-weekday {
  font-size: 0.72rem;
  font-weight: 400;
  color: #000;
  text-transform: none;
  letter-spacing: 0;
}

.sticky-col {
  position: sticky;
  background: #fff;
  z-index: 2;
  transition: background .12s ease;
}

.due-matrix thead .sticky-col {
  z-index: 4;
  background: #fafbfd;
}

.due-matrix tfoot .sticky-col {
  z-index: 4;
}

.empty-cell {
  text-align: center;
  padding: 2.5rem 1rem;
  background: #fff;
}

.empty-icon {
  font-size: 1.6rem;
  color: #000;
  opacity: .2;
  margin-bottom: 0.5rem;
  display: block;
}

.empty-title {
  font-size: 0.92rem;
  font-weight: 600;
  color: #000;
}

.empty-hint {
  font-size: 0.85rem;
  color: #000;
  margin-top: 2px;
}

.is-weekend {
  background: #fcfcfd;
}

.total-row th {
  position: sticky;
  bottom: 0;
  background: #f7f8fc;
  border-top: 1px solid #e7e9ef;
  border-bottom: 0;
  font-size: 0.85rem;
  font-weight: 700;
  color: #000;
  text-transform: none;
  letter-spacing: 0;
  z-index: 3;
}

.sticky-col-name {
  left: 0;
  min-width: 180px;
  max-width: 180px;
}

.is-sortable {
  cursor: pointer;
  user-select: none;
}

.is-sortable:hover {
  background: #f2f4f9;
}

.sort-arrow {
  font-size: .7rem;
  color: #7367f0;
}

.sticky-col-range {
  left: 180px;
  min-width: 110px;
  font-variant-numeric: tabular-nums;
}

.sticky-col-total {
  left: 290px;
  min-width: 120px;
  box-shadow: 1px 0 0 #eceef3;
  font-variant-numeric: tabular-nums;
}

.lifetime-due {
  font-weight: 600;
  color: #000;
}

.day-col {
  min-width: 92px;
  font-variant-numeric: tabular-nums;
}

.day-col.is-today {
  background: #f6f5ff;
}

.due-matrix thead .day-col.is-today {
  background: #efedff;
  color: #000;
}

.client-name {
  font-weight: 600;
  color: #3b3fc4;
  cursor: pointer;
}

.client-name:hover {
  text-decoration: underline;
}

.client-serial {
  color: #000;
  font-size: 0.82rem;
}

.client-mobile {
  color: #000;
  font-size: 0.82rem;
}

.due-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.25;
  color: #000 !important;
  cursor: pointer;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  transition: transform .12s ease, box-shadow .12s ease;
}

.due-cell:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(16, 24, 40, .14);
  filter: none;
}

.due-cell-meta {
  font-size: 0.7rem;
  font-weight: 500;
  color: #000;
}

.due-cell-flag {
  font-weight: 700;
  opacity: .7;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 3px 10px;
  border-radius: 14px;
  border: 1px solid #e6e7ee;
  background: #f7f8fb;
  font-size: 0.85rem;
  color: #000;
}

.filter-chip-label {
  font-weight: 600;
  color: #000;
}

.filter-chip-remove {
  border: 0;
  background: transparent;
  color: #000;
  opacity: .45;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  transition: opacity .12s ease;
}

.filter-chip-remove:hover {
  opacity: 1;
}

.filter-chip-clear {
  border: 0;
  background: transparent;
  color: #7367f0;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
}
</style>
